import React, { useEffect, useState, useRef } from 'react'
import { each } from 'lodash'
import { styled } from '@mui/material/styles'
import Container from '@mui/material/Container'
import Grid from '@mui/material/Grid'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Slider from '@mui/material/Slider'
import Paper from '@mui/material/Paper'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import InputAdornment from '@mui/material/InputAdornment'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'

import UploadIcon from '@mui/icons-material/Upload'
import ZoomInIcon from '@mui/icons-material/ZoomIn'
import ZoomOutIcon from '@mui/icons-material/ZoomOut'

import { Line, ComposedChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

import { AutoSaveProvider, useAutoSaveState, SaveButton, NameField } from '../lib/useAutoSaveState'
import toFixed from '../lib/toFixed.js'
import {
  drawPoint,
  drawLine,
  drawX
} from '../lib/canvas.js'
import {
  distance,
  calculateCEPRayleigh,
  standardDeviation,
  chiPDF,
  mannWhitneyTest,
  getFurthestDistance,
  getAvgFiveShotGroup,
  fTest
} from '../lib/stats.js'
import s3 from '../lib/s3.js'

const groupColors = [
  "#2C3E50", // navy blue.
  "#FF5733", // vibrant orange.
  "#C70039", // deep crimson.
  "#900C3F", // rich maroon.
  "#581845", // dark plum.
  "#FFC300", // bright yellow.
  "#DAF7A6", // light lime green.
  "#74C365", // medium grass green.
  "#116466", // deep teal.
  "#8E44AD", // royal purple.
  "#F1C40F", // golden yellow.
  "#E74C3C", // bright red.
  "#2ECC71", // vibrant green.
  "#3498DB", // bright blue.
  "#9B59B6", // muted purple.
  "#34495E", // dark blue.
  "#A3E4D7", // soft turquoise.
  "#F39C12", // burnt orange.
  "#7FB3D5", // light sky blue.
]

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
})

const ScrollableContainer = styled('div')({
  overflow: 'auto',
  maxWidth: '100%',
  maxHeight: '90vh',
})

function GroupComparison() {
  const [tab, setTab] = useState(0)
  const [target1, setTarget1] = useAutoSaveState('target1', {
    scale: '',
    bulletDiameter: '',
    bullseyeDiameter: ''
  })
  const [target2, setTarget2] = useAutoSaveState('target2', {
    scale: '',
    bulletDiameter: '',
    bullseyeDiameter: ''
  })
  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Grid container spacing={3}>
        <Grid container justifyContent="center" xs={12} sx={{ position: 'relative' }}>
          <Typography variant="h2" component="h1" style={{ textAlign: 'center' }}>
            Group Analysis
          </Typography>
          <SaveButton />
        </Grid>
        <NameField />
        <Grid item xs={12}>
          <Tabs value={tab} onChange={(e, value) => setTab(value)}>
            <Tab label="Target 1" />
            <Tab label="Target 2" />
            <Tab label="Stats" />
          </Tabs>
        </Grid>
        <Grid item xs={12} mt={2} hidden={tab !== 0}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
            <TargetImage target={target1} setTarget={setTarget1} imageUrl={target1.url} />
          </Paper>
        </Grid>
        <Grid item xs={12} mt={2} hidden={tab !== 1}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
            <TargetImage target={target2} setTarget={setTarget2} imageUrl={target2.url} />
          </Paper>
        </Grid>
        <Grid item xs={12} mt={2} hidden={tab !== 2}>
          <TargetStats target1={target1} target2={target2} hidden={tab !== 2} />
        </Grid>
      </Grid>
    </Container>
  )
}

function TargetStats ({ target1, target2, hidden }) {
  const group1Ref = useRef(null)
  const group2Ref = useRef(null)
  const [zoom, setZoom] = useState(1)
  const [charts1, setCharts1] = useState(null)
  const [charts2, setCharts2] = useState(null)
  const [combinedStats, setCombinedStats] = useState(null)
  const [stats1, setStats1] = useState(null)
  const [stats2, setStats2] = useState(null)

  const onZoomChange = e => {
    let zoom = (e.target.value - 9)
    if (zoom < 1) {
      zoom = 1 / (2 - zoom)
    }
    console.log({ zoom, value: e.target.value })
    setZoom(zoom)
  }

  const drawTarget = (target, canvas, setCharts, setStats, otherStats) => {
    const ctx = canvas.getContext('2d')
    console.log('h/w', canvas.height, canvas.width, canvas.offsetHeight, canvas.offsetWidth)
    console.log(canvas)
    canvas.height = canvas.offsetHeight
    canvas.width = canvas.offsetWidth
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, canvas.width, canvas.height)

    if (!target.scalePoints || target.scalePoints.length < 2 || !target.groups) return

    const scaleInches = parseFloat(target.scale)
    const scaleDist = distance(
      target.scalePoints[0].x,
      target.scalePoints[0].y,
      target.scalePoints[1].x,
      target.scalePoints[1].y
    )
    const pixelsPerInch = scaleDist / scaleInches
    const xdistances = []
    const ydistances = []
    const distances = []
    const allPoints = []

    let aggregateX = 0
    let aggregateY = 0
    let aggregateCount = 0
    each(target.groups, group => {
      each(group.impacts, impact => {
        aggregateX += impact.x - group.center.x
        aggregateY += impact.y - group.center.y
        ++aggregateCount
      })
    })
    aggregateX /= aggregateCount
    aggregateY /= aggregateCount

    const aggZoomX = canvas.width / 2 - aggregateX / zoom
    const aggZoomY = canvas.height / 2 - aggregateY / zoom
    drawX(ctx, { x: canvas.width / 2, y: canvas.height / 2 }, 10)
    drawX(ctx, { x: aggZoomX, y: aggZoomY }, 10)

    each(target.groups, (group, i) => {
      const allSingleShot = target.groups.every(g => g.impacts.length <= 1)
      const color = allSingleShot ? groupColors[0] : groupColors[i % groupColors.length]
      ctx.strokeStyle = color
      ctx.fillStyle = color
      each(group.impacts, impact => {
        const x = (impact.x - group.center.x - aggregateX)
        const y = (impact.y - group.center.y - aggregateY)
        const zoomX = (impact.x - group.center.x - aggregateX) / zoom + (canvas.width / 2)
        const zoomY = (impact.y - group.center.y - aggregateY) / zoom + (canvas.height / 2)

        allPoints.push({ x: x / pixelsPerInch, y: y / pixelsPerInch })
        distances.push(distance(impact.x - group.center.x, impact.y - group.center.y, aggregateX, aggregateY) / pixelsPerInch)
        xdistances.push((impact.x - group.center.x - aggregateX) / pixelsPerInch)
        ydistances.push((impact.y - group.center.y - aggregateY) / pixelsPerInch)

        ctx.beginPath()
        ctx.arc(zoomX, zoomY, 3, 0, 2 * Math.PI)
        ctx.fill()
      })
    })

    const cep50 = calculateCEPRayleigh(distances, 50)
    const cep95 = calculateCEPRayleigh(distances, 95)
    const cep99 = calculateCEPRayleigh(distances, 99)
    const cep999 = calculateCEPRayleigh(distances, 99.9)
    const drms = standardDeviation(distances, 0)
    const sd = standardDeviation(distances)
    const esDist = getFurthestDistance(allPoints)
    const meanRadius = distances.reduce((a, b) => a + b, 0) / distances.length
    const avgFiveShotGroup = getAvgFiveShotGroup(allPoints)

    each([
      { radius: cep50, text: 'CEP 50' },
      { radius: cep95, text: 'CEP 95' },
      { radius: cep99, text: 'CEP 99' },
      { radius: cep999, text: 'CEP 99.9' },
    ], ({ radius, text }) => {
      const centerX = canvas.width / 2
      const centerY = canvas.height / 2
      ctx.fillStyle = '#000000'
      ctx.font = "12px sans-serif";
      ctx.fillText(text, centerX - 20, centerY - (radius * pixelsPerInch) / zoom - 3)

      ctx.beginPath()
      ctx.arc(centerX, centerY, (radius * pixelsPerInch) / zoom, 0, 2 * Math.PI)
      ctx.strokeStyle = 'blue'
      ctx.stroke()
    })

    const numBuckets = 10
    const maxValue = Math.max.apply(Math, distances)
    const bucketSize = maxValue / numBuckets
    const buckets = new Array(numBuckets + 1).fill(0)

    each(distances, value => {
      const bucketIndex = Math.round(value / bucketSize)
      if (bucketIndex >= 0 && bucketIndex <= numBuckets) {
        buckets[bucketIndex]++
      }
    })

    const chart = []
    for (let i = 0; i <= numBuckets; i++) {
      const value = i * bucketSize
      const chi = chiPDF(value / (drms * .75), 1.6)
      const normal = chiPDF(value / drms, 1) * .75
      chart.push({
        x: value.toFixed(4),
        histogram: buckets[i],
        chi,
        normal
      })
    }
    setCharts(chart)
    setStats({
      count: distances.length,
      zeroErrorX: aggregateX / pixelsPerInch,
      zeroErrorY: aggregateY / pixelsPerInch,
      cep50,
      cep95,
      cep99,
      cep999,
      esDist,
      meanRadius,
      avgFiveShotGroup,
      drms,
      sd,
      distances,
      xdistances,
      ydistances
    })
    console.log({ cep50, cep95, cep99, cep999, esDist, sd, drms })
    if (otherStats) {
      const mww = mannWhitneyTest(distances, otherStats.distances)
      const verticalF = fTest(ydistances, otherStats.ydistances)
      const horizF = fTest(xdistances, otherStats.xdistances)
      const rms = Math.sqrt(
        Math.max(sd, otherStats.sd) ** 2 -
        Math.min(sd, otherStats.sd) ** 2
      )
      setCombinedStats({ mww, verticalF, horizF })
      console.log({ verticalF, horizF, mww, rms })
    }
    window.gtag('event', 'calculate-group-comparison')
  }

  useEffect(() => {
    drawTarget(target1, group1Ref.current, setCharts1, setStats1, stats2)
    drawTarget(target2, group2Ref.current, setCharts2, setStats2, stats1)
  }, [ target1, target2, zoom, hidden ])

  useEffect(() => {
    setTimeout(() => {
      drawTarget(target1, group1Ref.current, setCharts1, setStats1, stats2)
      drawTarget(target2, group2Ref.current, setCharts2, setStats2, stats1)
    }, 50)
  }, [ hidden ])

  return (
    <Grid container spacing={2}>
      <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }} elevation={6}>
        <Grid container>
          <Grid item xs={12}>
            <Typography variant="h3" style={{ textAlign: 'center' }}>
              Combined Groups
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="h6" style={{ textAlign: 'center' }}>
              {target1.filename}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="h6" style={{ textAlign: 'center' }}>
              {target2.filename}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <canvas ref={group1Ref} style={{ border: '1px solid #800', width: '100%', aspectRatio: '1 / 1' }} />
          </Grid>
          <Grid item xs={6}>
            <canvas ref={group2Ref} style={{ border: '1px solid #800', width: '100%', aspectRatio: '1 / 1' }} />
          </Grid>
          <Grid item xs={1}>
            <Grid container justifyContent="center">
              <ZoomInIcon />
            </Grid>
          </Grid>
          <Grid item xs={10}>
            <Slider defaultValue={10} step={.1} min={0} max={20} onChange={onZoomChange} />
          </Grid>
          <Grid item xs={1}>
            <Grid container justifyContent="center">
              <ZoomOutIcon />
            </Grid>
          </Grid>
        </Grid>
      </Paper>
      {(stats1 || stats2 || combinedStats) && <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', mt: 2, width: '100%' }} elevation={6}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="h3" style={{ textAlign: 'center' }}>
              Statistics
            </Typography>
          </Grid>
          <Grid item xs={6}>
            {stats1 && <Statistics data={stats1} />}
          </Grid>
          <Grid item xs={6}>
            {stats2 && <Statistics data={stats2} />}
          </Grid>
          {combinedStats && <>
            <Grid item xs={4}>
              <Card elevation={3}>
                <CardContent>
                  <Typography variant="h5" style={{ textAlign: 'center' }}>
                    MWW Test
                  </Typography>
                  <Alert severity={combinedStats.mww.p < .05 ? "success" : "error"}>
                    {combinedStats.mww.p < .05 ? "Significant" : "No significant"} difference found in group size.
                    {combinedStats.mww.p < .05 ? " (p < .05)" : " (p > .05)"}
                  </Alert>
                  <Grid container spacing={1} mt={1}>
                    <Grid item xs={6}>
                      <Typography color="textSecondary">P Value</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="h6">{toFixed(combinedStats.mww.p, 4)}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography color="textSecondary">U Value</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="h6">{toFixed(combinedStats.mww.u, 0)}</Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={4}>
              <Card elevation={3}>
                <CardContent>
                  <Typography variant="h5" style={{ textAlign: 'center' }}>
                    Vertical F Test
                  </Typography>
                  <Alert severity={combinedStats.verticalF.p < .05 ? "success" : "error"}>
                    {combinedStats.verticalF.p < .05 ? "Significant" : "No significant"} difference found in group vertical variation.
                    {combinedStats.verticalF.p < .05 ? " (p < .05)" : " (p > .05)"}
                  </Alert>
                  <Grid container spacing={1} mt={1}>
                    <Grid item xs={6}>
                      <Typography color="textSecondary">P Value</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="h6">{toFixed(combinedStats.verticalF.p, 4)}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography color="textSecondary">F Ratio</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="h6">{toFixed(combinedStats.verticalF.f, 4)}</Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={4}>
              <Card elevation={3}>
                <CardContent>
                  <Typography variant="h5" style={{ textAlign: 'center' }}>
                    Horizontal F Test
                  </Typography>
                  <Alert severity={combinedStats.horizF.p < .05 ? "success" : "error"}>
                    {combinedStats.horizF.p < .05 ? "Significant" : "No significant"} difference found in group horizontal variation.
                    {combinedStats.horizF.p < .05 ? " (p < .05)" : " (p > .05)"}
                  </Alert>
                  <Grid container spacing={1} mt={1}>
                    <Grid item xs={6}>
                      <Typography color="textSecondary">P Value</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="h6">{toFixed(combinedStats.horizF.p, 4)}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography color="textSecondary">F Ratio</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="h6">{toFixed(combinedStats.horizF.f, 4)}</Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </>}
        </Grid>
      </Paper>}
      {(charts1 || charts2) && <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', mt: 2, width: '100%' }} elevation={6}>
        <Grid container>
          <Grid item xs={12}>
            <Typography variant="h3" style={{ textAlign: 'center' }}>
              Distribution
            </Typography>
          </Grid>
          <Grid item xs={6}>
            {charts1 && <Histogram data={charts1} />}
          </Grid>
          <Grid item xs={6}>
            {charts2 && <Histogram data={charts2} />}
          </Grid>
        </Grid>
      </Paper>}
    </Grid>
  )
}

function Statistics ({ data }) {
  return (
    <Card elevation={3}>
      <CardContent>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <Typography color="textSecondary">Shots</Typography>
            <Typography variant="h6">{data.count}</Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography color="textSecondary">Extreme Spread</Typography>
            <Typography variant="h6">{toFixed(data.esDist, 3)}</Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography color="textSecondary">Mean Radius</Typography>
            <Typography variant="h6">{toFixed(data.meanRadius, 3)}</Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography color="textSecondary">Avg 5 Shot ES</Typography>
            <Typography variant="h6">{toFixed(data.avgFiveShotGroup, 3)}</Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography color="textSecondary">Y Zero Error</Typography>
            <Typography variant="h6">{toFixed(data.zeroErrorY, 3)}</Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography color="textSecondary">X Zero Error</Typography>
            <Typography variant="h6">{toFixed(data.zeroErrorX, 3)}</Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography color="textSecondary">SD</Typography>
            <Typography variant="h6">{toFixed(data.sd, 3)}</Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography color="textSecondary">CEP 50</Typography>
            <Typography variant="h6">{toFixed(data.cep50, 3)}</Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography color="textSecondary">CEP 95</Typography>
            <Typography variant="h6">{toFixed(data.cep95, 3)}</Typography>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  )
}

function Histogram ({ data }) {
  return (
    <ResponsiveContainer width="100%" height={400}>
      <ComposedChart data={data} margin={{ }}>
        <CartesianGrid />
        <XAxis dataKey="x" name="Distance From Center" domain={[ 0, Math.max(...Object.values(data).map(v => v.x)) ]} />
        <YAxis yAxisId="right" orientation="right" name="Probability" />
        <YAxis yAxisId="left" orientation="left" name="Count" />
        <Tooltip cursor={{ strokeDasharray: '3 3' }} />
        <Legend />
        <Bar yAxisId="left" dataKey="histogram" fill="#800" />
        <Line yAxisId="right" type="monotone" label="Chi Distribution" dataKey="chi" stroke="#00f" strokeWidth={3} dot={false} />
        <Line yAxisId="right" type="monotone" dataKey="normal" stroke="#f00" strokeWidth={3} dot={false} />
      </ComposedChart>
    </ResponsiveContainer>
  )
}

function TargetImage ({ target, setTarget, imageUrl }) {
  const canvasRef = useRef(null)
  const [img, setImg] = useState(false)

  useEffect(() => {
    if (imageUrl) {
      const newImg = new Image();
      newImg.onload = () => {
        setImg(newImg);
      };
      newImg.src = imageUrl;
    }
  }, [imageUrl]);

  useEffect(() => {
    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')
    if (img) {
      canvas.width = img.width
      canvas.height = img.height
      ctx.drawImage(img, 0, 0)
    }
    if (target.scalePoints) {
      if (target.scalePoints[0]) {
        drawPoint(ctx, target.scalePoints[0].x, target.scalePoints[0].y, 'green')
      }
      if (target.scalePoints[1]) {
        drawPoint(ctx, target.scalePoints[1].x, target.scalePoints[1].y, 'green')
        drawLine(
          ctx,
          target.scalePoints[0].x,
          target.scalePoints[0].y,
          target.scalePoints[1].x,
          target.scalePoints[1].y,
          'green'
        )
      }
      if (target.groups) {
        target.groups.forEach(group => {
          drawPoint(ctx, group.center.x, group.center.y, 'yellow', 4)
          drawPoint(ctx, group.center.x, group.center.y, 'blue')
          if (group.impacts) {
            group.impacts.forEach(impact => {
              drawPoint(ctx, impact.x, impact.y, 'red')
              drawLine(ctx, group.center.x, group.center.y, impact.x, impact.y)
            })
          }
        })
      }
    }
  }, [img, target])

  const handleImageChange = event => {
    const file = event.target.files[0]
    const reader = new FileReader()

    reader.onload = async () => {
      const newImg = new Image()
      newImg.onload = () => {
        setImg(newImg)
      }
      newImg.src = reader.result
      const id = Math.random().toString(36).slice(2)
      const uploadParams = {
        Bucket: 'bd-image-uploads', // Replace with your S3 bucket name
        Key: id, // Specify a key under which to store the file
        Body: file,
        ContentType: file.type
      }

      try {
        const data = await s3.upload(uploadParams).promise();
        console.log('Upload successful:', data.Location);
        setTarget({ ...target, url: data.Location, filename: file.name })
      } catch (err) {
        console.error('Upload failed:', err);
      }
    }
    reader.readAsDataURL(file)
  }

  const undo = () => {
    try {
      if (target.groups && target.groups[target.groups.length - 1] && target.groups[target.groups.length - 1].impacts.length) {
        console.log('undo impact')
        target.groups[target.groups.length - 1].impacts.pop()
      } else if (target.groups && target.groups.length) {
        console.log('undo group')
        target.groups.pop()
      } else {
        console.log('undo scale point')
        target.scalePoints.pop()
      }
      setTarget({ ...target })
    } catch (e) {
      console.log(e)
    }
  }

  const onCanvasClick = e => {
    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')
    const x = e.nativeEvent.layerX
    const y = e.nativeEvent.layerY
    if (!target.scalePoints) {
      setTarget({
        ...target,
        scalePoints: [ { x, y } ]
      })
    } else if (target.scalePoints.length < 2) {
      setTarget({
        ...target,
        scalePoints: [
          ...target.scalePoints,
          { x, y }
        ]
      })
    } else if (target.groups) {
      const groups = [ ...target.groups ]
      const group = groups[groups.length - 1]
      group.impacts.push({ x, y })
      setTarget({
        ...target,
        groups,
      })
    }
  }

  const onCanvasRightClick = e => {
    e.preventDefault()
    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')
    const x = e.nativeEvent.layerX
    const y = e.nativeEvent.layerY
    const group = {
      center: { x, y },
      impacts: []
    }
    const groups = target.groups ? [ ...target.groups ] : []
    groups.push(group)
    setTarget({
      ...target,
      groups
    })
  }

  let canvasCursor
  if (target.scale && target.scalePoints && target.scalePoints.length >= 2) {
    const scaleInches = parseFloat(target.scale)
    const scaleDist = distance(
      target.scalePoints[0].x,
      target.scalePoints[0].y,
      target.scalePoints[1].x,
      target.scalePoints[1].y
    )
    const pixelsPerInch = scaleDist / scaleInches
    const diameters = []
    if (target.bulletDiameter) {
      diameters.push(pixelsPerInch * parseFloat(target.bulletDiameter))
    }
    if (target.bullseyeDiameter) {
      diameters.push(pixelsPerInch * parseFloat(target.bullseyeDiameter))
    }
    if (diameters.length) {
      canvasCursor = getCircleCursor(diameters)
    }
  }

  return (
    <Grid container spacing={2} p={2}>
      <Grid item xs={12}>
        <Button fullWidth component="label" variant="contained" startIcon={<UploadIcon />}>
          Upload file
          <VisuallyHiddenInput type="file" onChange={handleImageChange} />
        </Button>
      </Grid>
      <Grid item xs={4}>
        <TextField
          fullWidth
          label={`Scale Size`}
          value={target.scale}
          onChange={e => setTarget({ ...target, scale: e.target.value })}
          InputProps={{ endAdornment: <InputAdornment position="end">inches</InputAdornment> }}
        />
      </Grid>
      <Grid item xs={4}>
        <TextField
          fullWidth
          label={`Bullet Diameter`}
          value={target.bulletDiameter}
          onChange={e => setTarget({ ...target, bulletDiameter: e.target.value })}
          InputProps={{ endAdornment: <InputAdornment position="end">inches</InputAdornment> }}
        />
      </Grid>
      <Grid item xs={4}>
        <TextField
          fullWidth
          label={`Bullseye Diameter`}
          value={target.bullseyeDiameter}
          onChange={e => setTarget({ ...target, bullseyeDiameter: e.target.value })}
          InputProps={{ endAdornment: <InputAdornment position="end">inches</InputAdornment> }}
        />
      </Grid>
      <Grid item xs={11}>
        <Alert variant="filled" severity="info">
          {!img && 'Upload a target image.'}
          {img && (!target.scalePoints || !target.scalePoints[0]) && 'Click on the first scale point.'}
          {img && target.scalePoints && !target.scalePoints[1] && 'Click on the second scale point.'}
          {img && target.scalePoints && target.scalePoints[1] && 'Right click on aimpoint, left click on bullet holes'}
        </Alert>
      </Grid>
      <Grid item xs={1}>
        <Button
          fullWidth
          variant="contained"
          sx={{ height: '100%' }}
          onClick={undo}
        >
          Undo
        </Button>
      </Grid>
      <Grid item xs={12}>
        <ScrollableContainer>
          <canvas ref={canvasRef} onClick={onCanvasClick} onContextMenu={onCanvasRightClick} style={{ cursor: canvasCursor }} />
        </ScrollableContainer>
      </Grid>
    </Grid>
  )
}

function getCircleCursor(cursorDiameters) {
  cursorDiameters = cursorDiameters.map(v => Math.min(v, 128))
  const maxDiameter = Math.max(...cursorDiameters)

  let svgCursor =
    "<svg xmlns='http://www.w3.org/2000/svg' width='" +
    maxDiameter +
    "' height='" +
    maxDiameter +
    "' viewport='0 0 " +
    maxDiameter +
    ' ' +
    maxDiameter +
    "' style='fill:none;stroke:red;stroke-width:1'>"

  cursorDiameters.forEach(diameter => {
      svgCursor += "<circle cx='" + maxDiameter / 2 + "' cy='" + maxDiameter / 2 + "' r='" + diameter / 2 + "' />";
  });
  svgCursor += '</svg>';

  const cursorStyle = 'url("data:image/svg+xml;utf8,' + svgCursor + '") ' + (maxDiameter / 2) + ' ' + (maxDiameter / 2) + ', auto';
  return cursorStyle
}

export default function WrappedGroupComparison() {
  return (
    <AutoSaveProvider>
      <GroupComparison />
    </AutoSaveProvider>
  )
}
